import { FC } from 'react';
import { useIntl } from 'react-intl';
import { generatePath, useParams } from 'react-router-dom';
import { noop } from 'lodash';
import cn from 'classnames';
import { useBoolean } from 'usehooks-ts';
import { faBookmark } from '@fortawesome/pro-duotone-svg-icons';
import { Button, createBemBlockBuilder } from '@ebsco-ui/ebsco-ui';

import {
  BackToRoute,
  ComponentSpinner,
  HoldsAndCheckoutsMessage,
  PageErrorMessage,
} from '@app/components';
import { useProjectDetails, useSavedSearchesFetch } from '@app/hooks';
import { sharedMessages } from '@app/translations';
import { Route } from '@app/constants';

import { useBatchInstancesFetch } from '../hooks/useBatchInstancesFetch';
import { HistoryRecordsPageWrapper } from '../HistoryRecordsPageWrapper';
import { EditProjectModal } from '../ProjectModals';
import { ProjectDetailsHeroSection } from './ProjectDetailsHeroSection';

import './ProjectDetails.scss';

const cnBem = createBemBlockBuilder(['projectDetails']);

export const ProjectsDetailsPage: FC = () => {
  const { $t } = useIntl();
  const { id: projectId = '' } = useParams();
  const projectDetailsQuery = useProjectDetails({ projectId });
  const projectRecordsQuery = useBatchInstancesFetch({
    queryKey: 'projectRecords',
    list: projectDetailsQuery?.data?.projectInstances,
  });
  const savedSearchesQuery = useSavedSearchesFetch();
  const {
    value: isEditProjectModalOpen,
    setTrue: openEditProjectModal,
    setFalse: closeEditProjectModal,
  } = useBoolean(false);

  if (
    projectDetailsQuery.isLoading ||
    (projectRecordsQuery.isLoading &&
      projectRecordsQuery.fetchStatus !== 'idle' &&
      !projectDetailsQuery.isError)
  ) {
    return <ComponentSpinner />;
  }

  if (projectDetailsQuery.isError) {
    return <PageErrorMessage onClick={projectDetailsQuery.refetch} />;
  }

  return (
    <div
      className={cn(cnBem(), 'container')}
      data-auto="projects-details-container"
    >
      <BackToRoute text="Projects" to={Route.projects} />
      <ProjectDetailsHeroSection
        title={projectDetailsQuery.data.name}
        description={projectDetailsQuery.data.description}
        openEditProjectModal={openEditProjectModal}
      />
      <HistoryRecordsPageWrapper
        parentRoute={Route.projectsDetails}
        isSearchesTabFirst={false}
        searchesTabConfig={{
          title: $t(sharedMessages.searches),
          documentTitle: $t({
            id: 'projects.searchHistoryDocumentTitle',
            defaultMessage: 'Searches - Projects - EBSCO Locate',
          }),
          itemSubtitle: $t(sharedMessages.search),
          route: generatePath(Route.projectsDetailsSearches, {
            id: projectId,
          }) as Route,
          list: projectDetailsQuery.data.projectSearches,
          savedSearchesList: savedSearchesQuery.data,
          onEntryRemoval: noop,
          emptyStateMessage: $t({
            id: 'projects.searches.emptyStateSearchMessage',
            defaultMessage: 'You currently have no searches for this project.',
          }),
          emptyStateIcon: faBookmark,
        }}
        recordsTabConfig={{
          title: $t(sharedMessages.records),
          documentTitle: $t({
            id: 'projects.viewedRecordsDocumentTitle',
            defaultMessage: 'Records - Projects - EBSCO Locate',
          }),
          route: generatePath(Route.projectsDetailsRecords, {
            id: projectId,
          }) as Route,
          list: projectRecordsQuery.data,
          isError: projectRecordsQuery.isError,
          actionButton: (
            <Button>
              <HoldsAndCheckoutsMessage
                requestsMessage={$t(sharedMessages.requestItem)}
                holdsMessage={$t(sharedMessages.placeHold)}
              />
            </Button>
          ),
          refetch: projectRecordsQuery.refetch,
          onEntryRemoval: noop,
          emptyStateMessage: $t({
            id: 'projects.records.emptyStateSearchMessage',
            defaultMessage: 'You currently have no records for this project.',
          }),
          emptyStateIcon: faBookmark,
        }}
      />
      <EditProjectModal
        isOpen={isEditProjectModalOpen}
        id={projectId}
        values={{
          name: projectDetailsQuery.data.name,
          description: projectDetailsQuery.data.description,
        }}
        onClose={closeEditProjectModal}
      />
    </div>
  );
};
